import type { ReviewFilters } from "../types/review";

/**
 * URL serialisation for a review session.
 *
 * A review is a conversation — "look at these frames" — and until now that
 * sentence could not be sent to anyone: the filters lived only in this
 * browser's localStorage, so a pasted /ml-exports/review link showed the
 * recipient whatever they happened to be looking at last.
 *
 * Everything that determines WHICH images the grid shows goes in the URL:
 * scope (location + machines), the time window, and every applied refinement.
 * Nothing that is merely a local view preference (cards per row, group
 * highlight, emphasis label) does — those belong to the viewer, not the link.
 */

export interface ReviewUrlState {
  locationId?: number;
  machineIds: number[];
  fromDate?: string;
  toDate?: string;
  filters: Omit<ReviewFilters, "start_time" | "end_time" | "location_id" | "machine_ids">;
}

/** Scope + window params, kept out of the generic filter passthrough. */
const LOCATION_PARAM = "location_id";
const MACHINES_PARAM = "machine_ids";
const FROM_PARAM = "start";
const TO_PARAM = "end";

/**
 * Filter keys carried in the URL. An explicit allowlist rather than "whatever
 * is on the object": it keeps pagination cursors (`limit`/`offset`) and any
 * future internal bookkeeping out of a link that is meant to describe a view,
 * and stops a malformed param from reaching the request model.
 */
const STRING_FILTER_KEYS = [
  "label_in",
  "review_state_in",
  "event_state_in",
  "validity_state_in",
  "alert_status_in",
  "alert_type_in",
  "alert_after",
  "alert_before",
  "review_presence",
  "reviewed_by",
  "reviewed_after",
  "reviewed_before",
  "ordering",
] as const;

const NUMBER_FILTER_KEYS = ["min_confidence", "max_confidence"] as const;

/** `2026-08-07` — a whole-day boundary in the viewer's own timezone. */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Serialise a window boundary.
 *
 * Day-aligned boundaries — what the top bar's day picker produces, and by far
 * the common case — are written as a bare date, so a shared link reads
 * `?start=2026-08-07&end=2026-08-09` instead of two 24-character timestamps.
 * The URL is meant to be pasted into a message and understood at a glance.
 *
 * Anything with a time component keeps full ISO. Truncating those would be a
 * silent lie: a 14:30–16:45 window would arrive at the recipient meaning a
 * different query than the sender ran, which defeats the entire point of
 * sharing the link.
 */
const formatBoundary = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const isLocalMidnight =
    d.getHours() === 0 &&
    d.getMinutes() === 0 &&
    d.getSeconds() === 0 &&
    d.getMilliseconds() === 0;
  if (!isLocalMidnight) return iso;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * Parse a boundary back to an ISO instant.
 *
 * A bare date resolves to LOCAL midnight, mirroring how it was written.
 * `new Date("2026-08-07")` would give UTC midnight instead, shifting the
 * window by the viewer's offset and breaking the round trip.
 */
const parseBoundary = (value: string): string | undefined => {
  const dateOnly = DATE_ONLY.exec(value);
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

/** Serialise a review session into search params. */
export const buildReviewSearchParams = (state: ReviewUrlState): URLSearchParams => {
  const params = new URLSearchParams();

  if (state.locationId != null) params.set(LOCATION_PARAM, String(state.locationId));
  if (state.machineIds.length > 0) {
    params.set(MACHINES_PARAM, state.machineIds.join(","));
  }
  if (state.fromDate) params.set(FROM_PARAM, formatBoundary(state.fromDate));
  if (state.toDate) params.set(TO_PARAM, formatBoundary(state.toDate));

  for (const key of STRING_FILTER_KEYS) {
    const value = state.filters[key];
    if (value != null && value !== "") params.set(key, String(value));
  }
  for (const key of NUMBER_FILTER_KEYS) {
    const value = state.filters[key];
    if (value != null) params.set(key, String(value));
  }
  if (state.filters.require_valid_flag === true) {
    params.set("require_valid_flag", "true");
  }

  return params;
};

/**
 * Parse search params back into a review session.
 *
 * Returns null when the URL carries no scope at all, which is the ordinary
 * "user just navigated here" case — the caller should then leave the persisted
 * store alone rather than clobbering it with an empty state.
 */
export const parseReviewSearchParams = (
  params: URLSearchParams,
): ReviewUrlState | null => {
  const hasLocation = params.has(LOCATION_PARAM);
  const hasMachines = params.has(MACHINES_PARAM);
  if (!hasLocation && !hasMachines) return null;

  const locationRaw = params.get(LOCATION_PARAM);
  const locationId = locationRaw != null ? Number(locationRaw) : undefined;

  const machineIds = (params.get(MACHINES_PARAM) ?? "")
    .split(",")
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n) && n > 0);

  const filters: ReviewUrlState["filters"] = {};
  for (const key of STRING_FILTER_KEYS) {
    const value = params.get(key);
    if (value) {
      // Widening through unknown keeps this one assignment honest instead of
      // casting every branch; the allowlist above is what makes it safe.
      (filters as Record<string, unknown>)[key] = value;
    }
  }
  for (const key of NUMBER_FILTER_KEYS) {
    const raw = params.get(key);
    if (raw == null) continue;
    const value = Number(raw);
    if (Number.isFinite(value)) filters[key] = value;
  }
  if (params.get("require_valid_flag") === "true") {
    filters.require_valid_flag = true;
  }

  const from = params.get(FROM_PARAM);
  const to = params.get(TO_PARAM);

  return {
    locationId: Number.isFinite(locationId) ? locationId : undefined,
    machineIds,
    fromDate: from ? parseBoundary(from) : undefined,
    toDate: to ? parseBoundary(to) : undefined,
    filters,
  };
};
